/**
 * QC Violations — Queue sub-tab.
 *
 * Pending / Approved / Rejected / Removed sub-status tabs. All approve /
 * reject / remove / change-status actions live here. Access is gated by
 * the QC Access Control settings (backend-enforced via 403).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  ShieldAlert,
  ExternalLink,
  Trash2,
  ArrowRightLeft,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

import { Button } from '@/components/ui/button';
import QCEvidence from '@/components/qc/QCEvidence';
import { IssueWarningButton } from '@/components/qc/IssueWarningButton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_TABS = [
  { key: 'pending_hr_approval', label: 'Pending', labelAr: 'قيد الانتظار' },
  { key: 'approved', label: 'Approved', labelAr: 'معتمد' },
  { key: 'rejected', label: 'Rejected', labelAr: 'مرفوض' },
  { key: 'unapproved_hr_removed', label: 'Removed', labelAr: 'مُزال' },
];

// Allowed target statuses per current status — mirrors the backend
// _ALLOWED_TRANSITIONS map. Same-status is filtered out at render time.
const ALLOWED_TARGETS = {
  pending_hr_approval:     ['approved', 'rejected'],
  approved:                ['pending_hr_approval', 'rejected', 'unapproved_hr_removed'],
  rejected:                ['pending_hr_approval', 'approved'],
  unapproved_hr_removed:   ['approved', 'pending_hr_approval'],
};

const severityBadge = (sev) => {
  const map = {
    low: 'bg-slate-100 text-slate-700',
    medium: 'bg-amber-100 text-amber-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
  };
  return map[sev] || 'bg-slate-100 text-slate-700';
};

// Origin badge — 'qc' (default) vs 'cctv' vs any unknown value ops may send.
// We show unknown values verbatim so an ops-side typo is visible.
const sourceLabel = (src, isRTL) => {
  const s = (src || 'qc').toLowerCase();
  if (s === 'qc')   return isRTL ? 'ضبط الجودة' : 'QC';
  if (s === 'cctv') return isRTL ? 'كاميرات المراقبة' : 'CCTV';
  return s.toUpperCase();
};

const sourceBadge = (src) => {
  const s = (src || 'qc').toLowerCase();
  if (s === 'cctv') return 'bg-violet-100 text-violet-800 border-violet-200';
  if (s === 'qc')   return 'bg-sky-100 text-sky-800 border-sky-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const statusBadge = (status) => {
  const map = {
    pending_hr_approval: 'bg-blue-100 text-blue-800',
    approved: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-rose-100 text-rose-800',
    unapproved_hr_removed: 'bg-slate-200 text-slate-700',
  };
  return map[status] || 'bg-slate-100 text-slate-700';
};

const statusLabel = (status, isRTL) => {
  const map = {
    pending_hr_approval: isRTL ? 'قيد الانتظار' : 'Pending',
    approved: isRTL ? 'معتمد' : 'Approved',
    rejected: isRTL ? 'مرفوض' : 'Rejected',
    unapproved_hr_removed: isRTL ? 'مُزال من الملف' : 'Removed from file',
  };
  return map[status] || status;
};

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const QCQueueTab = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const t = (en, ar) => (isRTL ? ar : en);

  const [statusFilter, setStatusFilter] = useState('pending_hr_approval');
  const [sourceFilter, setSourceFilter] = useState('all');   // all | qc | cctv
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [detail, setDetail] = useState(null);           // clicked row
  const [rejectFor, setRejectFor] = useState(null);     // { id, source_violation_id }
  const [rejectReason, setRejectReason] = useState('');
  const [removeFor, setRemoveFor] = useState(null);     // { id, source_violation_id }
  const [removeReason, setRemoveReason] = useState('');
  const [changeFor, setChangeFor] = useState(null);     // { id, status, source_violation_id }
  const [changeTarget, setChangeTarget] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const authorised = ['hr_manager', 'hr_admin', 'super_admin'].includes(user?.role);

  const fetchList = useCallback(async () => {
    if (!authorised) return;
    setLoading(true);
    setError(null);
    try {
      const params = { status: statusFilter, page_size: 200 };
      if (sourceFilter && sourceFilter !== 'all') params.source = sourceFilter;
      const r = await axios.get(`${API_URL}/qc-violations`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, token, authorised]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const doApprove = async (row) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/qc-violations/${row.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(t('Violation approved', 'تم اعتماد المخالفة'));
      setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const doReject = async () => {
    if (!rejectFor) return;
    if (rejectReason.trim().length < 10) {
      toast.error(t('Reason must be at least 10 characters',
        'يجب أن يكون السبب 10 أحرف على الأقل'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/qc-violations/${rejectFor.id}/reject`,
        { reason: rejectReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(t('Violation rejected', 'تم رفض المخالفة'));
      setRejectFor(null);
      setRejectReason('');
      setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(
        e.response?.data?.detail?.error ||
        (Array.isArray(e.response?.data?.detail) ? e.response.data.detail[0]?.msg : null) ||
        e.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const doRemove = async () => {
    if (!removeFor) return;
    if (removeReason.trim().length < 10) {
      toast.error(t('Reason must be at least 10 characters',
        'يجب أن يكون السبب 10 أحرف على الأقل'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/qc-violations/${removeFor.id}/remove`,
        { reason: removeReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(t('Violation removed from file', 'تم إزالة المخالفة من الملف'));
      setRemoveFor(null);
      setRemoveReason('');
      setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(
        e.response?.data?.detail?.error ||
        (Array.isArray(e.response?.data?.detail) ? e.response.data.detail[0]?.msg : null) ||
        e.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const doChangeStatus = async () => {
    if (!changeFor || !changeTarget) return;
    if (changeReason.trim().length < 10) {
      toast.error(t('Reason must be at least 10 characters',
        'يجب أن يكون السبب 10 أحرف على الأقل'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/qc-violations/${changeFor.id}/change-status`,
        { new_status: changeTarget, reason: changeReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(t('Status changed', 'تم تغيير الحالة'));
      setChangeFor(null);
      setChangeTarget('');
      setChangeReason('');
      setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(
        e.response?.data?.detail?.error ||
        (Array.isArray(e.response?.data?.detail) ? e.response.data.detail[0]?.msg : null) ||
        e.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openChange = (row) => {
    setChangeFor(row);
    const targets = ALLOWED_TARGETS[row.status] || [];
    setChangeTarget(targets[0] || '');
    setChangeReason('');
  };

  if (!authorised) {
    return (
      <div className="p-6" data-testid="qc-forbidden">
        <Card>
          <CardContent className="p-8 text-center text-slate-600">
            <ShieldAlert className="h-8 w-8 mx-auto mb-3 text-slate-400" />
            {t('You do not have permission to view QC Violations.',
               'ليس لديك صلاحية لعرض مخالفات ضبط الجودة.')}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'} data-testid="qc-queue-tab">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-slate-600 max-w-2xl">
          {t(
            'Review QC findings received from the ops platform. Approving attaches the item to the employee\'s file. Rejecting requires a reason. The system never computes or applies any deduction.',
            'راجع النتائج الواردة من منصة العمليات. الاعتماد يُلحق البند بملف الموظف. الرفض يتطلب سبباً. لا يقوم النظام باحتساب أو تطبيق أي خصم.'
          )}
        </p>
        <div className="flex items-center gap-2">
          <div className="min-w-[180px]">
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger data-testid="qc-source-filter">
                <SelectValue placeholder={t('Filter by source', 'تصفية حسب المصدر')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="qc-source-filter-all">
                  {t('All sources', 'كل المصادر')}
                </SelectItem>
                <SelectItem value="qc" data-testid="qc-source-filter-qc">
                  {t('QC only', 'ضبط الجودة فقط')}
                </SelectItem>
                <SelectItem value="cctv" data-testid="qc-source-filter-cctv">
                  {t('CCTV only', 'كاميرات المراقبة فقط')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={fetchList}
            disabled={loading}
            data-testid="qc-refresh-btn"
          >
            {loading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList data-testid="qc-status-tabs">
          {STATUS_TABS.map((s) => (
            <TabsTrigger
              key={s.key}
              value={s.key}
              data-testid={`qc-status-tab-${s.key}`}
            >
              {isRTL ? s.labelAr : s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {STATUS_TABS.map((s) => (
          <TabsContent key={s.key} value={s.key}>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {isRTL ? s.labelAr : s.label}
                  <span className="ml-2 text-sm font-normal text-slate-500" data-testid="qc-total-count">
                    ({total})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {error && (
                  <div className="text-sm text-rose-600 mb-3" data-testid="qc-error">{error}</div>
                )}
                {loading ? (
                  <div className="p-8 text-center text-slate-500">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="p-8 text-center text-slate-500" data-testid="qc-empty">
                    {t('Nothing to show here.', 'لا توجد بيانات لعرضها.')}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('Source', 'المصدر')}</TableHead>
                        <TableHead>{t('Employee', 'الموظف')}</TableHead>
                        <TableHead>{t('Violation', 'المخالفة')}</TableHead>
                        <TableHead>{t('Severity', 'الشدة')}</TableHead>
                        <TableHead>{t('Occurred', 'التاريخ')}</TableHead>
                        <TableHead>{t('Inspector', 'المفتش')}</TableHead>
                        <TableHead>{t('Deduction', 'خصم')}</TableHead>
                        <TableHead className="text-right">{t('Actions', 'إجراءات')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((row) => (
                        <TableRow
                          key={row.id}
                          data-testid={`qc-row-${row.id}`}
                        >
                          <TableCell>
                            <Badge
                              className={`border ${sourceBadge(row.source)}`}
                              data-testid={`qc-source-badge-${row.id}`}
                            >
                              {sourceLabel(row.source, isRTL)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-slate-900" data-testid={`qc-row-name-${row.id}`}>
                              {row.employee_snapshot?.full_name_ar || row.employee_id || '—'}
                            </div>
                            <div className="text-xs text-slate-500">
                              {row.employee_snapshot?.job_title || ''}
                            </div>
                            {/* 2026-02-17 — Government ID + Employee Number so
                                QC violations are never resolved on the name
                                alone. Falls back to '—' so the rows still
                                align when a field is missing. */}
                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-600">
                              <span data-testid={`qc-row-emp-number-${row.id}`}>
                                <span className="text-slate-400">
                                  {t('Emp #', 'الرقم الوظيفي')}:
                                </span>{' '}
                                {row.employee_snapshot?.employee_number || row.employee_number || '—'}
                              </span>
                              <span data-testid={`qc-row-gov-id-${row.id}`}>
                                <span className="text-slate-400">
                                  {row.employee_snapshot?.id_document_type === 'iqama'
                                    ? t('Iqama', 'رقم الإقامة')
                                    : t('National ID', 'رقم الهوية')}
                                  :
                                </span>{' '}
                                {row.employee_snapshot?.id_document_number || '—'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{row.violation_item}</div>
                            <div className="text-xs text-slate-500 line-clamp-1 max-w-xs">
                              {row.description}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={severityBadge(row.severity)} data-testid={`qc-severity-${row.id}`}>
                              {row.severity}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{formatWhen(row.occurred_at)}</TableCell>
                          <TableCell className="text-sm">{row.inspector_name}</TableCell>
                          <TableCell>
                            {row.deduction_recommended ? (
                              <Badge className="bg-amber-100 text-amber-800" data-testid={`qc-deduction-flag-${row.id}`}>
                                {t('Recommended', 'موصى به')}
                              </Badge>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDetail(row)}
                              data-testid={`qc-view-btn-${row.id}`}
                            >
                              {t('View', 'عرض')}
                            </Button>
                            {row.status === 'pending_hr_approval' && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => doApprove(row)}
                                  disabled={submitting}
                                  data-testid={`qc-approve-btn-${row.id}`}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-1" />
                                  {t('Approve', 'اعتماد')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => setRejectFor(row)}
                                  disabled={submitting}
                                  data-testid={`qc-reject-btn-${row.id}`}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  {t('Reject', 'رفض')}
                                </Button>
                              </>
                            )}
                            {row.status === 'approved' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setRemoveFor(row); setRemoveReason(''); }}
                                  disabled={submitting}
                                  data-testid={`qc-remove-btn-${row.id}`}
                                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  {t('Remove', 'إزالة')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openChange(row)}
                                  disabled={submitting}
                                  data-testid={`qc-change-btn-${row.id}`}
                                >
                                  <ArrowRightLeft className="h-4 w-4 mr-1" />
                                  {t('Change status', 'تغيير الحالة')}
                                </Button>
                                <IssueWarningButton violation={row} isRTL={isRTL} onIssued={fetchList} />
                              </>
                            )}
                            {(row.status === 'rejected' || row.status === 'unapproved_hr_removed') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openChange(row)}
                                disabled={submitting}
                                data-testid={`qc-change-btn-${row.id}`}
                              >
                                <ArrowRightLeft className="h-4 w-4 mr-1" />
                                {t('Change status', 'تغيير الحالة')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="qc-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detail.violation_item}
                  <Badge
                    className={`border ${sourceBadge(detail.source)}`}
                    data-testid="qc-detail-source-badge"
                  >
                    {sourceLabel(detail.source, isRTL)}
                  </Badge>
                  <Badge className={statusBadge(detail.status)}>{detail.status}</Badge>
                </DialogTitle>
                <DialogDescription>
                  {t('Source ID:', 'المعرف من المصدر:')}{' '}
                  <code className="text-xs">{detail.source_violation_id}</code>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 text-sm">
                <div data-testid="qc-detail-employee-block">
                  <div className="text-slate-500 text-xs">{t('Employee', 'الموظف')}</div>
                  <div className="font-medium" data-testid="qc-detail-name">
                    {detail.employee_snapshot?.full_name_ar || detail.employee_id || '—'}
                  </div>
                  <div className="text-xs text-slate-500">
                    {detail.employee_snapshot?.job_title || ''}
                  </div>
                  {/* 2026-02-17 — Full identity trio so QC violations are
                      resolvable without relying on the name. */}
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <div>
                      <span className="text-slate-500">{t('Employee #', 'الرقم الوظيفي')}: </span>
                      <span className="font-medium text-slate-800" data-testid="qc-detail-emp-number">
                        {detail.employee_snapshot?.employee_number || detail.employee_number || '—'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">
                        {detail.employee_snapshot?.id_document_type === 'iqama'
                          ? t('Iqama', 'رقم الإقامة')
                          : t('National ID', 'رقم الهوية')}
                        :{' '}
                      </span>
                      <span className="font-medium text-slate-800" data-testid="qc-detail-gov-id">
                        {detail.employee_snapshot?.id_document_number || '—'}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 text-xs">{t('Description', 'الوصف')}</div>
                  <div className="whitespace-pre-wrap">{detail.description}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-slate-500 text-xs">{t('Severity', 'الشدة')}</div>
                    <Badge className={severityBadge(detail.severity)}>{detail.severity}</Badge>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs">{t('Occurred', 'التاريخ')}</div>
                    <div>{formatWhen(detail.occurred_at)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs">{t('Inspector', 'المفتش')}</div>
                    <div>{detail.inspector_name}</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs">{t('Received', 'وقت الاستلام')}</div>
                    <div>{formatWhen(detail.received_at)}</div>
                  </div>
                </div>
                {detail.evidence_state !== 'none' && (
                  <div>
                    <div className="text-slate-500 text-xs mb-1.5">{t('Evidence', 'الدليل')}</div>
                    <div data-testid="qc-detail-evidence">
                      <QCEvidence
                        violation={detail}
                        token={token}
                        isRTL={isRTL}
                        testId="qc-detail-evidence"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-slate-500 text-xs">{t('Deduction', 'الخصم')}</div>
                  {detail.deduction_recommended ? (
                    <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                      <strong>{t('Advisory only.', 'إشعار توصية فقط.')}</strong>{' '}
                      {t(
                        'The ops platform recommends a deduction. This HR system does NOT compute, apply, or deduct any amount. Any deduction decision is made and processed outside this system.',
                        'توصي منصة العمليات بخصم. لا يقوم نظام الموارد البشرية هذا باحتساب أو تطبيق أو خصم أي مبلغ. يُتخذ قرار الخصم ويُنفذ خارج هذا النظام.'
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400">{t('No deduction recommended.', 'لا توصية بخصم.')}</div>
                  )}
                </div>

                {detail.status === 'rejected' && detail.reject_reason && (
                  <div>
                    <div className="text-slate-500 text-xs">{t('Reject reason', 'سبب الرفض')}</div>
                    <div className="whitespace-pre-wrap">{detail.reject_reason}</div>
                  </div>
                )}
                {detail.status === 'unapproved_hr_removed' && detail.last_status_change_reason && (
                  <div>
                    <div className="text-slate-500 text-xs">{t('Removal reason', 'سبب الإزالة')}</div>
                    <div className="whitespace-pre-wrap" data-testid="qc-detail-removal-reason">
                      {detail.last_status_change_reason}
                    </div>
                  </div>
                )}
                {detail.previous_status && detail.previous_status !== detail.status && (
                  <div className="text-xs text-slate-500" data-testid="qc-detail-prev-status">
                    {t('Previous status:', 'الحالة السابقة:')}{' '}
                    <Badge className={statusBadge(detail.previous_status)}>
                      {statusLabel(detail.previous_status, isRTL)}
                    </Badge>
                  </div>
                )}
                {detail.status !== 'pending_hr_approval' && detail.decided_by_email && (
                  <div className="text-xs text-slate-500">
                    {t('Decided by', 'قرر بواسطة')}{' '}
                    <strong>{detail.decided_by_email}</strong> {t('at', 'في')}{' '}
                    {formatWhen(detail.decided_at)}
                  </div>
                )}
              </div>

              <DialogFooter>
                {detail.status === 'pending_hr_approval' ? (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => setRejectFor(detail)}
                      disabled={submitting}
                      data-testid="qc-detail-reject-btn"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      {t('Reject', 'رفض')}
                    </Button>
                    <Button
                      onClick={() => doApprove(detail)}
                      disabled={submitting}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="qc-detail-approve-btn"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      {t('Approve', 'اعتماد')}
                    </Button>
                  </>
                ) : detail.status === 'approved' ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => { setRemoveFor(detail); setRemoveReason(''); }}
                      disabled={submitting}
                      data-testid="qc-detail-remove-btn"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('Remove from file', 'إزالة من الملف')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => openChange(detail)}
                      disabled={submitting}
                      data-testid="qc-detail-change-btn"
                    >
                      <ArrowRightLeft className="h-4 w-4 mr-1" />
                      {t('Change status', 'تغيير الحالة')}
                    </Button>
                    <Button onClick={() => setDetail(null)} data-testid="qc-detail-close-btn">
                      {t('Close', 'إغلاق')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => openChange(detail)}
                      disabled={submitting}
                      data-testid="qc-detail-change-btn"
                    >
                      <ArrowRightLeft className="h-4 w-4 mr-1" />
                      {t('Change status', 'تغيير الحالة')}
                    </Button>
                    <Button onClick={() => setDetail(null)} data-testid="qc-detail-close-btn">
                      {t('Close', 'إغلاق')}
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog
        open={!!rejectFor}
        onOpenChange={(v) => { if (!v) { setRejectFor(null); setRejectReason(''); } }}
      >
        <DialogContent data-testid="qc-reject-dialog">
          <DialogHeader>
            <DialogTitle>{t('Reject QC violation', 'رفض المخالفة')}</DialogTitle>
            <DialogDescription>
              {t(
                'Provide a reason (minimum 10 characters). This will be recorded in the audit log and sent back to ops.',
                'يرجى ذكر السبب (10 أحرف على الأقل). سيتم تسجيله في سجل التدقيق وإرساله إلى منصة العمليات.'
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('Reason...', 'السبب...')}
            data-testid="qc-reject-reason-input"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setRejectFor(null); setRejectReason(''); }}
              data-testid="qc-reject-cancel-btn"
            >
              {t('Cancel', 'إلغاء')}
            </Button>
            <Button
              variant="destructive"
              onClick={doReject}
              disabled={submitting || rejectReason.trim().length < 10}
              data-testid="qc-reject-submit-btn"
            >
              {submitting
                ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                : <XCircle className="h-4 w-4 mr-1" />}
              {t('Confirm reject', 'تأكيد الرفض')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove from employee's file dialog */}
      <Dialog
        open={!!removeFor}
        onOpenChange={(v) => { if (!v) { setRemoveFor(null); setRemoveReason(''); } }}
      >
        <DialogContent data-testid="qc-remove-dialog">
          <DialogHeader>
            <DialogTitle>{t('Remove violation from file', 'إزالة المخالفة من الملف')}</DialogTitle>
            <DialogDescription>
              {t(
                'This will take the violation off the employee\'s file but keep the record for history. It is fully reversible from the "Removed" tab. Reason is mandatory (minimum 10 characters) and will be recorded in the audit log.',
                'ستتم إزالة المخالفة من ملف الموظف مع الاحتفاظ بالسجل في التاريخ. يمكن الإرجاع من تبويب "المُزال". السبب مطلوب (10 أحرف على الأقل) وسيُسجَّل في سجل التدقيق.'
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={removeReason}
            onChange={(e) => setRemoveReason(e.target.value)}
            placeholder={t('Reason...', 'السبب...')}
            data-testid="qc-remove-reason-input"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setRemoveFor(null); setRemoveReason(''); }}
              data-testid="qc-remove-cancel-btn"
            >
              {t('Cancel', 'إلغاء')}
            </Button>
            <Button
              onClick={doRemove}
              disabled={submitting || removeReason.trim().length < 10}
              data-testid="qc-remove-submit-btn"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {submitting
                ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                : <Trash2 className="h-4 w-4 mr-1" />}
              {t('Confirm remove', 'تأكيد الإزالة')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change status dialog */}
      <Dialog
        open={!!changeFor}
        onOpenChange={(v) => {
          if (!v) {
            setChangeFor(null); setChangeTarget(''); setChangeReason('');
          }
        }}
      >
        <DialogContent data-testid="qc-change-dialog">
          <DialogHeader>
            <DialogTitle>{t('Change violation status', 'تغيير حالة المخالفة')}</DialogTitle>
            <DialogDescription asChild>
              <div>
                <div>
                  {t('Current status:', 'الحالة الحالية:')}{' '}
                  <Badge className={statusBadge(changeFor?.status)}>
                    {statusLabel(changeFor?.status, isRTL)}
                  </Badge>
                </div>
                <div className="mt-2 text-xs">
                  {t(
                    'Content (description, severity, inspector) is not editable — that data comes from the ops platform. Only the status can be changed here. Reason is mandatory (min 10 chars).',
                    'المحتوى (الوصف، الشدة، المفتش) غير قابل للتعديل — تلك البيانات مصدرها منصة العمليات. يمكن هنا تغيير الحالة فقط. السبب مطلوب (10 أحرف على الأقل).'
                  )}
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-slate-500 mb-1">{t('New status', 'الحالة الجديدة')}</div>
              <Select value={changeTarget} onValueChange={setChangeTarget}>
                <SelectTrigger data-testid="qc-change-target-select">
                  <SelectValue placeholder={t('Select target status', 'اختر الحالة الهدف')} />
                </SelectTrigger>
                <SelectContent>
                  {(ALLOWED_TARGETS[changeFor?.status] || []).map((st) => (
                    <SelectItem
                      key={st}
                      value={st}
                      data-testid={`qc-change-target-option-${st}`}
                    >
                      {statusLabel(st, isRTL)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              rows={4}
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder={t('Reason...', 'السبب...')}
              data-testid="qc-change-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setChangeFor(null); setChangeTarget(''); setChangeReason('');
              }}
              data-testid="qc-change-cancel-btn"
            >
              {t('Cancel', 'إلغاء')}
            </Button>
            <Button
              onClick={doChangeStatus}
              disabled={submitting || !changeTarget || changeReason.trim().length < 10}
              data-testid="qc-change-submit-btn"
            >
              {submitting
                ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                : <ArrowRightLeft className="h-4 w-4 mr-1" />}
              {t('Confirm change', 'تأكيد التغيير')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QCQueueTab;
